/**
 * API configuration
 * Contains all API endpoint definitions for the application
 */

// Base API URL - use environment variable for production
export const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/api";

// Authentication endpoints - ONLY auth routes use /v1/
export const AUTH_ENDPOINTS = {
  LOGIN: `${API_BASE_URL}/v1/auth/login`,
  REGISTER: `${API_BASE_URL}/v1/auth/register`,
  ONBOARDING: `${API_BASE_URL}/v1/auth/onboarding`,
  PROFILE: `${API_BASE_URL}/v1/auth/me`,
  // CRITICAL: Profile updates go through collaborators route (NO /v1/)
  // Only auth routes use /v1/, all other routes use /api/ directly
  UPDATE_PROFILE: `${API_BASE_URL}/collaborators/profile`,
  DELETE_ACCOUNT: `${API_BASE_URL}/v1/auth/delete-account`,
  REFRESH_TOKEN: `${API_BASE_URL}/v1/auth/refresh-token`,
  VERIFY_EMAIL: `${API_BASE_URL}/v1/auth/verify-email`,
  RESEND_VERIFICATION: `${API_BASE_URL}/v1/auth/resend-verification`,
  FORGOT_PASSWORD: `${API_BASE_URL}/v1/auth/forgot-password`,
  RESET_PASSWORD: `${API_BASE_URL}/v1/auth/reset-password`,
  GOOGLE_LOGIN: `${API_BASE_URL}/v1/auth/google`,
  GOOGLE_CALLBACK: `${API_BASE_URL}/v1/auth/google/callback`,
  
  // Academic Data Endpoints (for onboarding)
  ACADEMIC_FIELDS: `${API_BASE_URL}/v1/auth/academic-data/fields`,
  ACADEMIC_ROLES: `${API_BASE_URL}/v1/auth/academic-data/roles`,
  ACADEMIC_INTERESTS: `${API_BASE_URL}/v1/auth/academic-data/interests`,
  ACADEMIC_GOALS: `${API_BASE_URL}/v1/auth/academic-data/goals`,
  
  // Institution Verification Endpoints
  VERIFY_INSTITUTION_EMAIL: `${API_BASE_URL}/v1/auth/verify-institution-email`,
  SEARCH_INSTITUTIONS: `${API_BASE_URL}/v1/auth/search-institutions`,
};

// Papers endpoints
export const PAPERS_ENDPOINTS = {
  SEARCH: `${API_BASE_URL}/papers/search`,
  BOOKMARK: `${API_BASE_URL}/papers/bookmark`,
  BOOKMARKS: `${API_BASE_URL}/papers/bookmarks`,
  PAPER_DETAIL: (id) => `${API_BASE_URL}/papers/${id}`,
};

// Feed endpoints
export const FEED_ENDPOINTS = {
  POSTS: `${API_BASE_URL}/feed/posts`,
  POST_DETAIL: (id) => `${API_BASE_URL}/feed/posts/${id}`,
  LIKE_POST: (id) => `${API_BASE_URL}/feed/posts/${id}/like`,
  SAVE_POST: (id) => `${API_BASE_URL}/feed/posts/${id}/save`,
  COMMENTS: (id) => `${API_BASE_URL}/feed/posts/${id}/comments`,
  SHARE: (id) => `${API_BASE_URL}/feed/posts/${id}/share`,
};

// Research rooms endpoints
export const ROOMS_ENDPOINTS = {
  // Room CRUD
  LIST: `${API_BASE_URL}/research-rooms`,
  FIELDS: `${API_BASE_URL}/research-rooms/fields`,
  MY_ROOMS: `${API_BASE_URL}/research-rooms/my-rooms`,
  CREATE: `${API_BASE_URL}/research-rooms`,
  DETAIL: (id) => `${API_BASE_URL}/research-rooms/${id}`,
  UPDATE: (id) => `${API_BASE_URL}/research-rooms/${id}`,
  DELETE: (id) => `${API_BASE_URL}/research-rooms/${id}`,

  // Membership
  JOIN: (id) => `${API_BASE_URL}/research-rooms/${id}/join`,
  LEAVE: (id) => `${API_BASE_URL}/research-rooms/${id}/leave`,
  HANDLE_REQUEST: (roomId, userId) =>
    `${API_BASE_URL}/research-rooms/${roomId}/requests/${userId}`,
  REMOVE_MEMBER: (roomId, userId) =>
    `${API_BASE_URL}/research-rooms/${roomId}/members/${userId}`,

  // Messages (Phase 2)
  MESSAGES: (roomId) => `${API_BASE_URL}/research-rooms/${roomId}/messages`,
  SEND_MESSAGE: (roomId) => `${API_BASE_URL}/research-rooms/${roomId}/messages`,
  EDIT_MESSAGE: (roomId, messageId) =>
    `${API_BASE_URL}/research-rooms/${roomId}/messages/${messageId}`,
  DELETE_MESSAGE: (roomId, messageId) =>
    `${API_BASE_URL}/research-rooms/${roomId}/messages/${messageId}`,
  ADD_REACTION: (roomId, messageId) =>
    `${API_BASE_URL}/research-rooms/${roomId}/messages/${messageId}/reactions`,
  PIN_MESSAGE: (roomId, messageId) =>
    `${API_BASE_URL}/research-rooms/${roomId}/messages/${messageId}/pin`,
  PINNED_MESSAGES: (roomId) =>
    `${API_BASE_URL}/research-rooms/${roomId}/messages/pinned`,
  SEARCH_MESSAGES: (roomId) =>
    `${API_BASE_URL}/research-rooms/${roomId}/messages/search`,
  MESSAGE_CONTEXT: (roomId, messageId) =>
    `${API_BASE_URL}/research-rooms/${roomId}/messages/${messageId}/context`,
  LOAD_MORE: (roomId) =>
    `${API_BASE_URL}/research-rooms/${roomId}/messages/load-more`,
  MARK_READ: (roomId) =>
    `${API_BASE_URL}/research-rooms/${roomId}/messages/mark-read`,
  UNREAD_COUNT: (roomId) =>
    `${API_BASE_URL}/research-rooms/${roomId}/messages/unread-count`,

  // Tasks (Phase 4)
  TASKS: {
    CREATE: `${API_BASE_URL}/tasks`,
    LIST: (roomId) => `${API_BASE_URL}/tasks/room/${roomId}`,
    KANBAN: (roomId) => `${API_BASE_URL}/tasks/room/${roomId}/kanban`,
    MY_TASKS: (roomId) => `${API_BASE_URL}/tasks/my-tasks/${roomId}`,
    OVERDUE: (roomId) => `${API_BASE_URL}/tasks/overdue/${roomId}`,
    STATS: (roomId) => `${API_BASE_URL}/tasks/room/${roomId}/stats`,
    DETAIL: (id) => `${API_BASE_URL}/tasks/${id}`,
    UPDATE: (id) => `${API_BASE_URL}/tasks/${id}`,
    UPDATE_STATUS: (id) => `${API_BASE_URL}/tasks/${id}/status`,
    DELETE: (id) => `${API_BASE_URL}/tasks/${id}`,
    TOGGLE_SUBTASK: (taskId, subtaskId) =>
      `${API_BASE_URL}/tasks/${taskId}/subtasks/${subtaskId}/toggle`,
    ADD_COMMENT: (id) => `${API_BASE_URL}/tasks/${id}/comments`,
    ATTACH_FILE: (id) => `${API_BASE_URL}/tasks/${id}/attachments`,
  },

  // Files & Attachments (Phase 3)
  ATTACHMENTS: {
    LIST: (roomId) => `${API_BASE_URL}/research-rooms/${roomId}/attachments`,
    UPLOAD_URL: (roomId) =>
      `${API_BASE_URL}/research-rooms/${roomId}/attachments/upload-url`,
    CREATE: (roomId) => `${API_BASE_URL}/research-rooms/${roomId}/attachments`,
    DETAIL: (roomId, attachmentId) =>
      `${API_BASE_URL}/research-rooms/${roomId}/attachments/${attachmentId}`,
    DOWNLOAD: (roomId, attachmentId) =>
      `${API_BASE_URL}/research-rooms/${roomId}/attachments/${attachmentId}/download`,
    VERSIONS: (roomId, attachmentId) =>
      `${API_BASE_URL}/research-rooms/${roomId}/attachments/${attachmentId}/versions`,
    SEARCH: (roomId) =>
      `${API_BASE_URL}/research-rooms/${roomId}/attachments/search`,
  },

  // Library/Resources (Phase 3)
  RESOURCES: {
    LIST: (roomId) => `${API_BASE_URL}/research-rooms/${roomId}/resources`,
    CREATE: (roomId) => `${API_BASE_URL}/research-rooms/${roomId}/resources`,
    DETAIL: (roomId, resourceId) =>
      `${API_BASE_URL}/research-rooms/${roomId}/resources/${resourceId}`,
    UPDATE: (roomId, resourceId) =>
      `${API_BASE_URL}/research-rooms/${roomId}/resources/${resourceId}`,
    DELETE: (roomId, resourceId) =>
      `${API_BASE_URL}/research-rooms/${roomId}/resources/${resourceId}`,
    FOLDERS: (roomId) =>
      `${API_BASE_URL}/research-rooms/${roomId}/resources/folders`,
  },

  // Calendar (Phase 5)
  CALENDAR: {
    LIST: (roomId) => `${API_BASE_URL}/research-rooms/${roomId}/calendar`,
    CREATE: (roomId) => `${API_BASE_URL}/research-rooms/${roomId}/calendar`,
    DETAIL: (roomId, eventId) =>
      `${API_BASE_URL}/research-rooms/${roomId}/calendar/${eventId}`,
    UPDATE: (roomId, eventId) =>
      `${API_BASE_URL}/research-rooms/${roomId}/calendar/${eventId}`,
    DELETE: (roomId, eventId) =>
      `${API_BASE_URL}/research-rooms/${roomId}/calendar/${eventId}`,
    RSVP: (roomId, eventId) =>
      `${API_BASE_URL}/research-rooms/${roomId}/calendar/${eventId}/rsvp`,
  },
};

// User endpoints
export const USER_ENDPOINTS = {
  PROFILE: (id) => `${API_BASE_URL}/users/${id}`,
  NOTIFICATIONS: `/notifications`,
  SETTINGS: `${API_BASE_URL}/users/settings`,
  SUGGESTED: `${API_BASE_URL}/users/suggested`,
  FOLLOW: (id) => `${API_BASE_URL}/users/${id}/follow`,
  USER_POSTS: (id) => `${API_BASE_URL}/users/${id}/posts`,
};

// Freelance endpoints
export const FREELANCE_ENDPOINTS = {
  TASKS: `${API_BASE_URL}/freelance/tasks`,
  TASK_DETAIL: (id) => `${API_BASE_URL}/freelance/tasks/${id}`,
  POSTED_TASKS: `${API_BASE_URL}/freelance/tasks/posted`,
  APPLIED_TASKS: `${API_BASE_URL}/freelance/tasks/applied`,
  APPLY_TO_TASK: (id) => `${API_BASE_URL}/freelance/tasks/${id}/apply`,
  UPDATE_APPLICATION: (taskId, appId) =>
    `${API_BASE_URL}/freelance/tasks/${taskId}/applications/${appId}`,
  PROFILE: `${API_BASE_URL}/freelance/profile`,
  REVIEWS: `${API_BASE_URL}/freelance/reviews`,
  REVIEW: (id) => `${API_BASE_URL}/freelance/reviews/${id}`,
};

// Messaging endpoints
export const MESSAGING_ENDPOINTS = {
  CONTACTS: `${API_BASE_URL}/messaging/contacts`,
  CONVERSATIONS: `${API_BASE_URL}/messaging/conversations`,
  CONVERSATION_DETAIL: (id) => `${API_BASE_URL}/messaging/conversations/${id}`,
  MESSAGES: (conversationId) =>
    `${API_BASE_URL}/messaging/conversations/${conversationId}/messages`,
  SEND_MESSAGE: (conversationId) =>
    `${API_BASE_URL}/messaging/conversations/${conversationId}/messages`,
  UPLOAD_FILE: (conversationId) =>
    `${API_BASE_URL}/messaging/conversations/${conversationId}/files`,
  READ_MESSAGES: (conversationId) =>
    `${API_BASE_URL}/messaging/conversations/${conversationId}/read`,
  START_CALL: (conversationId, type) =>
    `${API_BASE_URL}/messaging/conversations/${conversationId}/call/${type}`,
  END_CALL: (callId) => `${API_BASE_URL}/messaging/calls/${callId}/end`,
};

// Communities endpoints
export const COMMUNITIES_ENDPOINTS = {
  // Community management
  CREATE: `${API_BASE_URL}/communities`,
  LIST: `${API_BASE_URL}/communities`,
  DETAIL: (id) => `${API_BASE_URL}/communities/${id}`,
  UPDATE: (id) => `${API_BASE_URL}/communities/${id}`,
  DELETE: (id) => `${API_BASE_URL}/communities/${id}`,

  // NEW: Preview endpoint with analytics, activity, etc.
  PREVIEW: (id) => `${API_BASE_URL}/communities/${id}/preview`,

  // NEW: Analytics and activity endpoints
  ANALYTICS: (id) => `${API_BASE_URL}/communities/${id}/analytics`,
  ACTIVITY: (id) => `${API_BASE_URL}/communities/${id}/activity`,
  TRENDING: (id) => `${API_BASE_URL}/communities/${id}/trending`,

  // NEW: Guidelines endpoints
  GUIDELINES: (id) => `${API_BASE_URL}/communities/${id}/guidelines`,
  GUIDELINES_TEMPLATE: `${API_BASE_URL}/communities/guidelines/template`,

  // NEW: Rating endpoints
  RATING: (id) => `${API_BASE_URL}/communities/${id}/rating`,
  RATING_SUMMARY: (id) => `${API_BASE_URL}/communities/${id}/rating/summary`,
  MY_RATING: (id) => `${API_BASE_URL}/communities/${id}/rating/my-rating`,
  ALL_RATINGS: (id) => `${API_BASE_URL}/communities/${id}/rating/all`,
  BATCH_RATING_SUMMARY: `${API_BASE_URL}/communities/rating/batch-summary`,

  // Membership
  JOIN: (id) => `${API_BASE_URL}/communities/${id}/join`,
  LEAVE: (id) => `${API_BASE_URL}/communities/${id}/leave`,
  JOIN_REQUESTS: (id) => `${API_BASE_URL}/communities/${id}/join-requests`,
  HANDLE_JOIN_REQUEST: (communityId, userId, action) =>
    `${API_BASE_URL}/communities/${communityId}/join-requests/${userId}/${action}`,
  MEMBERS: (id) => `${API_BASE_URL}/communities/${id}/members`,
  REMOVE_MEMBER: (communityId, memberId) =>
    `${API_BASE_URL}/communities/${communityId}/members/${memberId}`,
  PROMOTE_MEMBER: (communityId, memberId) =>
    `${API_BASE_URL}/communities/${communityId}/members/${memberId}/promote`,
  DEMOTE_MODERATOR: (communityId, moderatorId) =>
    `${API_BASE_URL}/communities/${communityId}/moderators/${moderatorId}/demote`,

  // Feed endpoints (Phase 2 - Reddit-style feed)
  FEED: `${API_BASE_URL}/communities/feed`,
  FEED_POPULAR: `${API_BASE_URL}/communities/feed/popular`,
  FEED_TRENDING: `${API_BASE_URL}/communities/feed/trending`,
  FEED_STATS: `${API_BASE_URL}/communities/feed/stats`,
  SAVE_THREAD: (threadId) =>
    `${API_BASE_URL}/communities/feed/threads/${threadId}/save`,
  UNSAVE_THREAD: (threadId) =>
    `${API_BASE_URL}/communities/feed/threads/${threadId}/save`,
  SAVED_THREADS: `${API_BASE_URL}/communities/feed/saved`,
  SAVED_THREAD_IDS: `${API_BASE_URL}/communities/feed/saved/ids`,
  SAVED_COLLECTIONS: `${API_BASE_URL}/communities/feed/saved/collections`,
  FEED_SIDEBAR: `${API_BASE_URL}/communities/feed/sidebar`,
  COMMUNITY_SPECIFIC_SIDEBAR: (id) => `${API_BASE_URL}/communities/${id}/sidebar`,

  // Threads
  THREADS: (id) => `${API_BASE_URL}/communities/${id}/threads`,
  THREAD_DETAIL: (communityId, threadId) =>
    `${API_BASE_URL}/communities/${communityId}/threads/${threadId}`,
  THREAD_PHOTO_UPLOAD_URL: (id) =>
    `${API_BASE_URL}/communities/${id}/threads/upload-photo`,
  THREAD_PHOTO: (communityId, photoKey) =>
    `${API_BASE_URL}/communities/${communityId}/threads/photos/${photoKey}`,
  VOTE_THREAD: (communityId, threadId) =>
    `${API_BASE_URL}/communities/${communityId}/threads/${threadId}/vote`,
  DOWNVOTE_THREAD: (communityId, threadId) =>
    `${API_BASE_URL}/communities/${communityId}/threads/${threadId}/downvote`,
  PIN_THREAD: (communityId, threadId) =>
    `${API_BASE_URL}/communities/${communityId}/threads/${threadId}/pin`,
  LOCK_THREAD: (communityId, threadId) =>
    `${API_BASE_URL}/communities/${communityId}/threads/${threadId}/lock`,

  // Replies
  REPLIES: (communityId, threadId) =>
    `${API_BASE_URL}/communities/${communityId}/threads/${threadId}/replies`,
  REPLY_DETAIL: (communityId, threadId, replyId) =>
    `${API_BASE_URL}/communities/${communityId}/threads/${threadId}/replies/${replyId}`,
  VOTE_REPLY: (communityId, threadId, replyId) =>
    `${API_BASE_URL}/communities/${communityId}/threads/${threadId}/replies/${replyId}/vote`,

  // Messages
  MESSAGES: (id) => `${API_BASE_URL}/communities/${id}/messages`,
  MESSAGE_DETAIL: (communityId, messageId) =>
    `${API_BASE_URL}/communities/${communityId}/messages/${messageId}`,
  SEARCH_MESSAGES: (id) => `${API_BASE_URL}/communities/${id}/messages/search`,

  // Rooms
  ROOMS: (id) => `${API_BASE_URL}/communities/${id}/rooms`,
  ROOM_DETAIL: (communityId, roomId) =>
    `${API_BASE_URL}/communities/${communityId}/rooms/${roomId}`,
  CREATE_ROOM: (communityId) =>
    `${API_BASE_URL}/communities/${communityId}/rooms`,
  JOIN_ROOM: (communityId, roomId) =>
    `${API_BASE_URL}/communities/${communityId}/rooms/${roomId}/join`,
  ROOM_JOIN: (communityId, roomId) =>
    `${API_BASE_URL}/communities/${communityId}/rooms/${roomId}/join`,
  LEAVE_ROOM: (communityId, roomId) =>
    `${API_BASE_URL}/communities/${communityId}/rooms/${roomId}/leave`,
  HANDLE_ROOM_JOIN_REQUEST: (communityId, roomId, userId, action) =>
    `${API_BASE_URL}/communities/${communityId}/rooms/${roomId}/join-requests/${userId}/${action}`,
  REMOVE_ROOM_MEMBER: (communityId, roomId, memberId) =>
    `${API_BASE_URL}/communities/${communityId}/rooms/${roomId}/members/${memberId}`,

  // Photos
  PHOTO_UPLOAD_URL: (id) =>
    `${API_BASE_URL}/communities/${id}/photo/upload-url`,
  PHOTO: (id) => `${API_BASE_URL}/communities/${id}/photo`,
  BULK_VALIDATE_PHOTOS: `${API_BASE_URL}/communities/photos/validate-bulk`,

  // Discovery endpoints (Phase 3)
  DISCOVER_HOMEPAGE: `${API_BASE_URL}/communities/discover`,
  DISCOVER_TRENDING: `${API_BASE_URL}/communities/discover/trending`,
  DISCOVER_POPULAR: `${API_BASE_URL}/communities/discover/popular`,
  DISCOVER_RECOMMENDED: `${API_BASE_URL}/communities/discover/recommended`,
  DISCOVER_SEARCH: `${API_BASE_URL}/communities/discover/search`,
  DISCOVER_BY_FIELD: (field) =>
    `${API_BASE_URL}/communities/discover/by-field/${encodeURIComponent(
      field
    )}`,
  DISCOVER_BY_TOPIC: (topic) =>
    `${API_BASE_URL}/communities/discover/by-topic/${encodeURIComponent(
      topic
    )}`,
  DISCOVER_TOPICS: `${API_BASE_URL}/communities/discover/topics`,
  DISCOVER_FIELDS: `${API_BASE_URL}/communities/discover/fields`,

  // Moderation endpoints (Phase 5)
  MODERATION_REPORTS: (id) =>
    `${API_BASE_URL}/communities/${id}/moderation/reports`,
  MODERATION_REPORT_ACTION: (communityId, reportId) =>
    `${API_BASE_URL}/communities/${communityId}/moderation/reports/${reportId}/action`,
  MODERATION_ACTIONS: (id) =>
    `${API_BASE_URL}/communities/${id}/moderation/logs`,

  // Analytics endpoints (Phase 5)
  MY_STATS: (id) => `${API_BASE_URL}/communities/${id}/my-stats`,
  LEADERBOARD: (id) => `${API_BASE_URL}/communities/${id}/leaderboard`,
  GLOBAL_STATS: `${API_BASE_URL}/communities/my-global-stats`,
};

// External API endpoints
export const EXTERNAL_ENDPOINTS = {
  SEMANTIC_SCHOLAR: {
    SEARCH: "https://api.semanticscholar.org/graph/v1/paper/search",
    PAPER_DETAIL: (id) =>
      `https://api.semanticscholar.org/graph/v1/paper/${id}`,
    CORS_PROXY: "https://corsproxy.io/?",
  },
};
